import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, ChevronLeft, ChevronRight, Circle,
  X, ZoomIn, BarChart3,
  TrendingUp, PieChart, DollarSign, Mail, Car, Home, Gem, Bell, Shield, Smartphone, Monitor
} from 'lucide-react';

const APP_NAME = "PortfolioAsaurus";
const HERO_TITLE = "Your Entire Portfolio. One Dashboard.";
const HERO_SUBTITLE = "Track stocks, crypto, real estate, vehicles, jewelry and more — all in one beautiful, real-time dashboard.";
const CTA_HEADLINE = "Ready to Take Control of Your Portfolio?";
const CTA_SUBTITLE = "Request access today and start tracking everything you own in one place.";
const CTA_BUTTON_TEXT = "Request Access";
const CTA_LINK = "/?requestAccess=true";
const BACK_LINK = "/";
const AUTO_PLAY_INTERVAL = 5000;

const slides = [
  {
    image: "/screenshots/slide1_overview.png",
    title: "Complete Dashboard Overview",
    desc: "Every section of your portfolio at a glance — market performance, live treemap, diversity breakdown, platform tracking, stock analytics, and non-market assets all in one view.",
    icon: BarChart3,
    accent: "from-blue-500 to-cyan-500"
  },
  {
    image: "/screenshots/slide2_market.png",
    title: "Live Market Dashboard",
    desc: "Real-time treemap visualization of your entire portfolio with market performance returns. See gainers and losers at a glance with color-coded tiles sized by position value.",
    icon: TrendingUp,
    accent: "from-emerald-500 to-teal-500"
  },
  {
    image: "/screenshots/slide3_fullscreen.png",
    title: "Full-Screen Market View",
    desc: "Immersive trading experience with live market movers, world market clocks, crypto news ticker, and curated market news — all alongside your portfolio treemap.",
    icon: Monitor,
    accent: "from-indigo-500 to-purple-500"
  },
  {
    image: "/screenshots/slide4_diversity.png",
    title: "Portfolio Diversity Breakdown",
    desc: "Understand your asset allocation across stocks, real estate, crypto, cash, vehicles, and more with interactive pie charts, detailed breakdowns, and market vs non-market analysis.",
    icon: PieChart,
    accent: "from-violet-500 to-purple-500"
  },
  {
    image: "/screenshots/slide5_vehicles.png",
    title: "Vehicle Tracking",
    desc: "Track your entire vehicle collection with photo galleries, VIN numbers, mileage, cost basis, and current valuations — all with beautiful card layouts.",
    icon: Car,
    accent: "from-amber-500 to-orange-500"
  },
  {
    image: "/screenshots/slide6_jewelry.png",
    title: "Jewelry & Collectibles",
    desc: "Catalog your watch collection, jewelry, and collectibles with high-res photos. Filter by brand, material, and type — with full cost and value tracking.",
    icon: Gem,
    accent: "from-rose-500 to-pink-500"
  },
  {
    image: "/screenshots/slide7_electronics.png",
    title: "Electronics & Audio",
    desc: "Track cameras, computers, headphones, speakers, and hi-fi equipment. Full inventory management with photos, specs, and depreciation tracking.",
    icon: Smartphone,
    accent: "from-cyan-500 to-blue-500"
  },
  {
    image: "/screenshots/slide8_realestate.png",
    title: "Real Estate Portfolio",
    desc: "Manage your real estate holdings with aerial and street views, property details, purchase prices, and current market values across all your properties.",
    icon: Home,
    accent: "from-green-500 to-emerald-500"
  }
];

const mobileScreens = [
  { image: "/screenshots/mob1_fingerprint.png", label: "Biometric Login" },
  { image: "/screenshots/mob2_overview.png", label: "Dashboard" },
  { image: "/screenshots/mob3_treemap.png", label: "Market Live" },
  { image: "/screenshots/mob4_diversity.png", label: "Diversity" },
  { image: "/screenshots/mob5_vehicle.png", label: "Vehicle Detail" },
];

const features = [
  { icon: TrendingUp, title: "Live Market Data", desc: "Real-time stock prices with S&P, NDX, DOW tracking" },
  { icon: PieChart, title: "Asset Diversity", desc: "Visual breakdown across all investment types" },
  { icon: Home, title: "Real Estate", desc: "Track properties with addresses, values & appreciation" },
  { icon: Car, title: "Vehicles", desc: "Log vehicles with VIN, mileage & depreciation" },
  { icon: Gem, title: "Collectibles", desc: "Jewelry, art, firearms & electronics tracking" },
  { icon: DollarSign, title: "Gain/Loss", desc: "Cost basis tracking with real-time P&L" },
  { icon: Mail, title: "Daily Summaries", desc: "Automated email reports with privacy controls" },
  { icon: Bell, title: "Performance", desc: "Track returns across 1D, 1W, 1M, 1Y, and all time" },
  { icon: Shield, title: "Secure Access", desc: "PIN login, auto-logout & biometric support" },
  { icon: BarChart3, title: "Admin Panel", desc: "Full user management and system controls" },
  { icon: Smartphone, title: "Mobile Ready", desc: "Responsive design works on any device" },
  { icon: DollarSign, title: "Multi-Platform", desc: "Track across brokers — Schwab, Fidelity & more" },
];

export default function LearnMore() {
  const navigate = useNavigate();
  const [current, setCurrent] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(AUTO_PLAY_INTERVAL > 0);
  const [direction, setDirection] = useState(1);
  const [lightboxIdx, setLightboxIdx] = useState(null);

  const allImages = [
    ...slides.map((s) => ({ src: s.image, title: s.title, desc: s.desc })),
    ...mobileScreens.map((m) => ({ src: m.image, title: m.label + " (Mobile)", desc: "Full-featured mobile experience" })),
  ];

  const openLightbox = (imgSrc) => {
    const idx = allImages.findIndex(i => i.src === imgSrc);
    setLightboxIdx(idx >= 0 ? idx : 0);
  };

  const lightboxPrev = () => setLightboxIdx(i => (i - 1 + allImages.length) % allImages.length);
  const lightboxNext = () => setLightboxIdx(i => (i + 1) % allImages.length);

  const goTo = useCallback((idx) => {
    setDirection(idx > current ? 1 : -1);
    setCurrent(idx);
    setIsAutoPlaying(false);
  }, [current]);

  const next = useCallback(() => {
    setDirection(1);
    setCurrent(c => (c + 1) % slides.length);
  }, []);

  const prev = useCallback(() => {
    setDirection(-1);
    setCurrent(c => (c - 1 + slides.length) % slides.length);
    setIsAutoPlaying(false);
  }, []);

  useEffect(() => {
    if (!isAutoPlaying || AUTO_PLAY_INTERVAL <= 0) return;
    const timer = setInterval(next, AUTO_PLAY_INTERVAL);
    return () => clearInterval(timer);
  }, [isAutoPlaying, next]);

  const slide = slides[current];
  const Icon = slide.icon;

  return (
    <div className="min-h-screen bg-[#0a0e1a] text-white">
      {/* Nav */}
      <nav className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
        <button onClick={() => navigate(BACK_LINK)} data-testid="learn-more-back-btn" className="text-gray-400 hover:text-white transition-colors text-sm flex items-center gap-2">
          <ArrowLeft size={16} /> Back
        </button>
        <div className="text-lg font-bold bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent">
          {APP_NAME}
        </div>
        <button onClick={() => navigate(CTA_LINK)} data-testid="learn-more-request-access-nav-btn" className="px-4 py-1.5 bg-blue-600 hover:bg-blue-500 text-white text-sm font-medium rounded-lg transition-colors">
          {CTA_BUTTON_TEXT}
        </button>
      </nav>

      {/* Hero */}
      <div className="text-center px-4 pt-6 pb-10">
        <h1 className="text-4xl sm:text-5xl font-bold mb-3 bg-gradient-to-r from-blue-400 via-cyan-300 to-blue-400 bg-clip-text text-transparent">
          {HERO_TITLE}
        </h1>
        <p className="text-base text-gray-400 max-w-xl mx-auto">{HERO_SUBTITLE}</p>
      </div>

      {/* Desktop Slideshow */}
      <div className="max-w-6xl mx-auto px-4 pb-12">
        <div className="relative rounded-2xl overflow-hidden bg-gray-900/50 border border-gray-700/40 shadow-2xl">
          <div className="flex flex-col lg:flex-row">
            <div className="lg:w-2/3 relative bg-black/30">
              <div className="aspect-[16/10] relative overflow-hidden">
                <div key={current} className="w-full h-full cursor-pointer group/img" onClick={() => openLightbox(slide.image)} style={{ animation: 'fadeSlide 0.4s ease-out' }}>
                  <img src={slide.image} alt={slide.title} className="w-full h-full object-cover object-top" />
                  <div className="absolute inset-0 bg-black/0 group-hover/img:bg-black/20 transition-all flex items-center justify-center opacity-0 group-hover/img:opacity-100">
                    <div className="bg-black/60 backdrop-blur-sm rounded-full px-4 py-2 flex items-center gap-2">
                      <ZoomIn size={18} className="text-white" />
                      <span className="text-white text-sm font-medium">Click to expand</span>
                    </div>
                  </div>
                </div>
                <div className="hidden lg:block absolute inset-y-0 right-0 w-24 bg-gradient-to-l from-gray-900/90 to-transparent pointer-events-none" />
              </div>
              <button onClick={prev} data-testid="slide-prev-btn" className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/50 hover:bg-black/70 flex items-center justify-center transition-colors backdrop-blur-sm">
                <ChevronLeft size={20} />
              </button>
              <button onClick={() => { next(); setIsAutoPlaying(false); }} data-testid="slide-next-btn" className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/50 hover:bg-black/70 flex items-center justify-center transition-colors backdrop-blur-sm">
                <ChevronRight size={20} />
              </button>
            </div>

            <div className="lg:w-1/3 p-6 lg:p-8 flex flex-col justify-center bg-gradient-to-b lg:bg-gradient-to-r from-gray-900/60 to-gray-900/90">
              <div key={current} style={{ animation: 'fadeUp 0.4s ease-out' }}>
                <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-gradient-to-r ${slide.accent} bg-opacity-20 mb-4`} style={{ background: 'linear-gradient(135deg, rgba(59,130,246,0.15), rgba(6,182,212,0.15))' }}>
                  <Icon size={16} className="text-blue-400" />
                  <span className="text-xs font-semibold text-blue-300 uppercase tracking-wider">{current + 1} of {slides.length}</span>
                </div>
                <h2 className="text-2xl font-bold mb-3 text-white">{slide.title}</h2>
                <p className="text-gray-400 leading-relaxed text-sm">{slide.desc}</p>
              </div>
            </div>
          </div>

          <div className="flex items-center justify-center gap-2 py-3 bg-gray-900/80 border-t border-gray-800/50 flex-wrap">
            {slides.map((s, i) => (
              <button key={i} onClick={() => goTo(i)} data-testid={`slide-dot-${i}`} className="group flex items-center gap-1.5 px-2 py-1 rounded-md transition-all hover:bg-gray-800/50">
                <Circle size={8} className={`transition-all ${i === current ? 'fill-blue-400 text-blue-400' : 'fill-gray-600 text-gray-600 group-hover:fill-gray-400 group-hover:text-gray-400'}`} />
                <span className={`text-xs hidden sm:inline transition-colors ${i === current ? 'text-blue-400 font-medium' : 'text-gray-500 group-hover:text-gray-400'}`}>{s.title}</span>
              </button>
            ))}
          </div>
        </div>

        {isAutoPlaying && (
          <div className="mt-3 flex items-center justify-center">
            <div className="h-0.5 w-32 bg-gray-800 rounded-full overflow-hidden">
              <div key={current} className="h-full bg-blue-500 rounded-full" style={{ animation: `progressBar ${AUTO_PLAY_INTERVAL}ms linear` }} />
            </div>
          </div>
        )}
      </div>

      {/* Mobile Experience Section */}
      <div className="max-w-5xl mx-auto px-4 pb-16" data-testid="mobile-section">
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-cyan-500/10 border border-cyan-500/20 mb-4">
            <Smartphone size={16} className="text-cyan-400" />
            <span className="text-xs font-semibold text-cyan-300 uppercase tracking-wider">Mobile Ready</span>
          </div>
          <h2 className="text-2xl sm:text-3xl font-bold text-white mb-2">Beautiful On Every Device</h2>
          <p className="text-gray-400 text-sm max-w-lg mx-auto">Full-featured mobile experience with live market data, treemaps, and portfolio diversity — no app download needed.</p>
        </div>

        <div className="flex items-end justify-start sm:justify-center gap-3 sm:gap-5 lg:gap-6 overflow-x-auto pb-4 px-4 sm:px-0 snap-x snap-mandatory sm:snap-none sm:overflow-visible">
          {mobileScreens.map((screen, i) => {
            const heights = ['310px', '350px', '380px', '350px', '310px'];
            return (
              <div
                key={i}
                className="cursor-pointer group/phone flex-shrink-0 snap-center"
                onClick={() => openLightbox(screen.image)}
                style={{ height: heights[i], width: 'auto' }}
              >
                <div className="h-full aspect-[9/19.5] bg-gray-950 rounded-[1.8rem] border-[3px] border-gray-600 overflow-hidden shadow-2xl shadow-blue-500/10 relative group-hover/phone:border-blue-400/60 transition-all group-hover/phone:shadow-blue-500/20">
                  <div className="absolute top-0 left-1/2 -translate-x-1/2 w-20 h-5 bg-gray-950 rounded-b-2xl z-10" />
                  <img src={screen.image} alt={screen.label} className="w-full h-full object-cover object-top" />
                  <div className="absolute inset-0 bg-black/0 group-hover/phone:bg-black/30 transition-all flex items-center justify-center opacity-0 group-hover/phone:opacity-100">
                    <ZoomIn size={24} className="text-white drop-shadow-lg" />
                  </div>
                </div>
                <p className="text-center text-xs text-gray-500 mt-2 group-hover/phone:text-gray-300 transition-colors">{screen.label}</p>
              </div>
            );
          })}
        </div>
      </div>

      {/* Features Grid */}
      <div className="max-w-5xl mx-auto px-4 pb-14">
        <h2 className="text-xl font-bold text-center mb-8 text-gray-200">Everything You Need</h2>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
          {features.map((f, i) => (
            <div key={i} className="bg-gray-900/40 border border-gray-700/30 rounded-xl p-4 text-center hover:border-blue-500/40 transition-all">
              <f.icon className="mx-auto text-blue-400 mb-2" size={24} />
              <h3 className="font-semibold text-sm mb-1 text-gray-200">{f.title}</h3>
              <p className="text-gray-500 text-xs leading-snug">{f.desc}</p>
            </div>
          ))}
        </div>
      </div>

      {/* CTA */}
      <div className="max-w-2xl mx-auto px-4 pb-16 text-center">
        <div className="bg-gradient-to-r from-blue-900/20 to-cyan-900/20 border border-blue-700/25 rounded-2xl p-8">
          <h2 className="text-xl font-bold mb-2">{CTA_HEADLINE}</h2>
          <p className="text-gray-400 text-sm mb-5">{CTA_SUBTITLE}</p>
          <button onClick={() => navigate(CTA_LINK)} data-testid="learn-more-request-access-cta-btn" className="px-8 py-3 bg-blue-600 hover:bg-blue-500 text-white font-semibold rounded-xl transition-colors shadow-lg shadow-blue-600/20">
            {CTA_BUTTON_TEXT}
          </button>
        </div>
      </div>

      {/* Lightbox */}
      {lightboxIdx !== null && (
        <div className="fixed inset-0 z-50 flex items-center justify-center" onClick={() => setLightboxIdx(null)}
          style={{ animation: 'fadeIn 0.2s ease-out' }}
          onKeyDown={(e) => { if (e.key === 'ArrowRight') lightboxNext(); else if (e.key === 'ArrowLeft') lightboxPrev(); else if (e.key === 'Escape') setLightboxIdx(null); }}
          tabIndex={0} ref={(el) => el && el.focus()}>
          <div className="absolute inset-0 bg-black/90 backdrop-blur-sm" />
          <button onClick={(e) => { e.stopPropagation(); lightboxPrev(); }} className="absolute left-3 sm:left-6 top-1/2 -translate-y-1/2 z-10 w-12 h-12 rounded-full bg-black/60 hover:bg-white/20 border border-white/20 flex items-center justify-center transition-colors">
            <ChevronLeft size={24} className="text-white" />
          </button>
          <button onClick={(e) => { e.stopPropagation(); lightboxNext(); }} className="absolute right-3 sm:right-6 top-1/2 -translate-y-1/2 z-10 w-12 h-12 rounded-full bg-black/60 hover:bg-white/20 border border-white/20 flex items-center justify-center transition-colors">
            <ChevronRight size={24} className="text-white" />
          </button>
          <div className="relative max-w-[90vw] max-h-[90vh] mx-16 flex flex-col" onClick={(e) => e.stopPropagation()}>
            <img key={lightboxIdx} src={allImages[lightboxIdx].src} alt="Expanded view" className="max-w-full max-h-[70vh] sm:max-h-[80vh] object-contain rounded-lg shadow-2xl" style={{ animation: 'zoomIn 0.25s ease-out' }} />
            <button onClick={() => setLightboxIdx(null)} className="absolute -top-3 -right-3 w-10 h-10 bg-gray-800 hover:bg-gray-700 border border-gray-600 rounded-full flex items-center justify-center transition-colors shadow-lg z-10">
              <X size={20} className="text-white" />
            </button>
            <div className="mt-2 bg-gray-900/90 backdrop-blur-sm p-3 rounded-lg flex items-center justify-between">
              <div className="min-w-0">
                <p className="text-white text-sm font-medium truncate">{allImages[lightboxIdx].title}</p>
                <p className="text-gray-400 text-xs mt-0.5 line-clamp-2">{allImages[lightboxIdx].desc}</p>
              </div>
              <span className="text-gray-500 text-xs whitespace-nowrap ml-3 flex-shrink-0">{lightboxIdx + 1} / {allImages.length}</span>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes fadeSlide {
          from { opacity: 0; transform: translateX(${direction > 0 ? '20px' : '-20px'}); }
          to { opacity: 1; transform: translateX(0); }
        }
        @keyframes fadeUp {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes progressBar {
          from { width: 0%; }
          to { width: 100%; }
        }
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes zoomIn {
          from { opacity: 0; transform: scale(0.92); }
          to { opacity: 1; transform: scale(1); }
        }
      `}</style>
    </div>
  );
}
